import * as Layout from "@shared/ui/wheelScreenerLayout";
import {
  displayTableColumns,
  ensureColumnPickerBesideReset,
  getTableColumnDefaults,
  renderTablePinnedAttr,
  type ColumnState,
} from "@shared/ui/tableColumns";
import {
  compactSubline,
  isWebCompactMode,
  resolveWebTableColumns,
} from "@shared/ui/webCompactMode";
import {
  accountShortCellHtml,
  brokerMarkCellHtml,
  ensureLedgerMarkIndex,
  recommendationLedgerMark,
  subscribeLedgerMarks,
} from "@shared/ui/ledgerMarks";
import { scopeAllowsRecommendation, subscribeAccountScope } from "@shared/ui/accountScope";
import { fmtPremiumPerDay, recommendationProfitPct } from "@shared/ui/profitability";
import { renderFocusRecRowMenu } from "@shared/ui/focusActions";
import {
  renderScreenerExpiryInnerHtml,
  screenerExpiryDateLabel,
  screenerExpiryEarningsTip,
} from "@shared/ui/screenerExpiryCell";
import { renderScreenerSharesCellHtml } from "@shared/ui/screenerSharesCell";
import { indexBadgesForTicker, renderIndexBadgesHtml } from "@shared/ui/indexMembership";
import { wheelGet } from "@shared/api/wheelClient";
import {
  earningsDateTip,
  earningsWithinExpiry,
  fmtEarningsDate,
  fmtEarningsInDays,
  fmtNum,
  fmtUsd,
  fmtWheelRatio,
  invalidateWheelSnapshots,
  loadPositionsSnapshot,
  loadRecommendationsSnapshot,
  normalizeTicker,
  peekPositionsSnapshot,
  peekRecommendationsSnapshot,
  sharesForTicker,
  type PositionsSnapshot,
  type WheelRecommendation,
} from "@shared/ui/watchlistData";
import { paintPanelLoadError } from "./authUi";
import {
  ADVICE_UPDATED_EVENT,
  adviceBadgeHtml,
  adviceRowAttrs,
  adviceSeedToScreenerRow,
  advisoryFilterFieldHtml,
  appendSyntheticAdviceRows,
  bindScreenerAdvice,
  applyAdviceMarks,
  pinAdvisedScreenerRows,
  screenerAdviceButtonHtml,
  screenerContractKey,
  screenerRowIsAdvised,
} from "@shared/ui/agentAdviceOverlay";
import { morphTableCell, reconcileTableBodyByKey, rowFromHtml } from "@shared/ui/tableRowPatch";
import { enrichAdviceScreenerRow, hydrateAdviceScreenerRows } from "@shared/ui/adviceScreenerEnrich";
import {
  invalidatePutsTabCashCardCache,
  putsTabCashCardHtml,
  refreshPutsTabCashCard,
} from "./putsTabCashCard";
import { fetchAgenticSnapshot } from "@shared/api/agenticClient";
import { loadAvailableCashFromAgenticSnapshot } from "@shared/ui/agenticPortfolioCash";
import {
  guardTickerFilterInputs,
  sanitizeTickerFilterValue,
  TICKER_FILTER_INPUT_ATTRS,
} from "@shared/shared/tickerFilterInput";
import { teardownMultiSelectPortals } from "./multiSelectPortals";

const PUT_FILTERS_KEY = "sfe-web-put-filters-v1";
const PUT_STARRED_KEY = "sfe-web-put-starred-v1";
const PUT_STARRED_FILTER_KEY = "sfe-web-put-starred-filter-v1";
const PUT_CASH_LIMIT_FILTER_KEY = "sfe-web-put-cash-limit-filter-v1";
const TABLE_ID = "web-puts-screener" as const;
const PUTS_CASH_CACHE_MS = 60_000;

const PUT_DTE_BUCKETS = [
  { key: "1-7", min: 1, max: 7, label: "1–7" },
  { key: "8-15", min: 8, max: 15, label: "8–15" },
  { key: "16-24", min: 16, max: 24, label: "16–24" },
  { key: "25-35", min: 25, max: 35, label: "25–35" },
];

const DTE_OPTIONS = PUT_DTE_BUCKETS.map((bucket) => ({
  value: bucket.key,
  label: bucket.label,
}));

const INDEX_OPTIONS = [
  { value: "none", label: "No index" },
  { value: "sp500", label: "S&P 500" },
  { value: "ndx", label: "NASDAQ" },
  { value: "dji", label: "DJI" },
  { value: "rut", label: "Russell" },
];

const RUBRIC_OPTIONS = [
  { value: "Good", label: Layout.displayDecision("Good") },
  { value: "Selective", label: Layout.displayDecision("Selective") },
  { value: "Small size", label: Layout.displayDecision("Small size") },
  { value: "Avoid", label: Layout.displayDecision("Avoid") },
  { value: "Insufficient data", label: Layout.displayDecision("Insufficient data") },
  { value: "__none__", label: "No report" },
  { value: "__unavailable__", label: "Band unavailable" },
  { value: "__unknown__", label: "Lookup unavailable" },
  { value: "__other__", label: "Unmapped band" },
];

const CAP_OPTIONS = [
  { value: "mega", label: "Mega · ≥ $200B" },
  { value: "large", label: "Large · $10B–$200B" },
  { value: "mid", label: "Mid · $2B–$10B" },
  { value: "small", label: "Small · $0.3B–$2B" },
  { value: "micro", label: "Micro · $0.05B–$0.3B" },
  { value: "nano", label: "Nano · under $0.05B" },
  { value: "unknown", label: "Unknown" },
];

const EXTRA_COLUMN_META: Record<
  string,
  {
    label: string;
    num?: boolean;
    heldTh?: boolean;
    hint?: string;
    compactHint?: string;
    sortKey?: string;
    sortType?: string;
  }
> = {
  next_earnings_date: {
    label: "Earnings",
    sortKey: "next_earnings_date",
    sortType: "str",
    hint: "Next earnings date for the underlying (via Finviz). Subline: days until that date; amber when it lands on or before expiry.",
  },
  broker_mark: {
    label: "Br",
    sortKey: "broker_mark",
    sortType: "str",
    hint: "Broker. RH is Robinhood, TT Tastytrade, WB Webull, FD Fidelity. Show, hide, or move it in column settings.",
  },
  account_short: {
    label: "Ac",
    sortKey: "account_short",
    sortType: "str",
    hint: "Short account name from Settings. It starts as a shortened account name and you can rename it.",
  },
};

export interface PutFilterState {
  quote: "tradable" | "all";
  dte: string[];
  earnings: "before" | "all";
  maxCollateral: number;
  ticker: string;
  sectors: string[];
  rubricBands: string[];
  indexes: string[];
  caps: string[];
  minProfitPct: number;
}

let starredTickers = new Set<string>();
let starredFilterOn = false;
let cashLimitFilterOn = false;
let cachedAvailableCash: number | null = null;
let cachedAvailableCashAt = 0;
let availableCashInflight: Promise<number> | null = null;

interface PutsPanelCache {
  allRows: WheelRecommendation[];
  positions: PositionsSnapshot;
  fetchedAt: number;
}

let putsPanelCache: PutsPanelCache | null = null;
let putsPanelInflight: Promise<PutsPanelCache> | null = null;
let putsMountedPanel: HTMLElement | null = null;
let putsRerender: (() => void) | null = null;
subscribeAccountScope(() => putsRerender?.());
subscribeLedgerMarks(() => {
  putsRerender?.();
});
const PUTS_CACHE_MS = 60_000;

if (typeof window !== "undefined") {
  window.addEventListener(ADVICE_UPDATED_EVENT, () => {
    if (putsMountedPanel?.isConnected && putsMountedPanel.getAttribute("data-wheel-view") === "puts") {
      putsRerender?.();
    }
  });
}

function syncStarredFromStorage(): void {
  starredFilterOn = loadStarredFilterOn();
  cashLimitFilterOn = loadCashLimitFilterOn();
  starredTickers = loadLocalStarred();
}

function putCollateral(rec: WheelRecommendation): number {
  return Number(rec.collateral) || (Number(rec.strike) || 0) * 100;
}

async function loadAvailableCash(force = false): Promise<number> {
  if (
    !force &&
    cachedAvailableCash != null &&
    Date.now() - cachedAvailableCashAt < PUTS_CASH_CACHE_MS
  ) {
    return cachedAvailableCash;
  }
  if (availableCashInflight && !force) return availableCashInflight;

  availableCashInflight = loadAvailableCashFromAgenticSnapshot(fetchAgenticSnapshot)
    .then((cash) => {
      cachedAvailableCash = cash;
      cachedAvailableCashAt = Date.now();
      return cash;
    })
    .finally(() => {
      availableCashInflight = null;
    });

  return availableCashInflight;
}

function refreshStarredTickersInBackground(onUpdated?: () => void): void {
  void wheelGet<{ tickers?: string[] }>("/starred-tickers", false, 8_000)
    .then((res) => {
      starredTickers = new Set((res.tickers ?? []).map((t) => t.toUpperCase()));
      saveLocalStarred(starredTickers);
      onUpdated?.();
    })
    .catch(() => {
      /* keep local starred set */
    });
}

function esc(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function escAttr(value: string): string {
  return esc(value).replaceAll("'", "&#39;");
}

function isTradableQuote(r: WheelRecommendation): boolean {
  const mid = Number(r.mid);
  const last = Number(r.last_price);
  if (!(mid > 0) || !(last > 0)) return false;
  const ref = Math.max(mid, last, 0.01);
  if (Math.abs(mid - last) / ref > 0.15 && Math.abs(mid - last) > 0.1) return false;
  const bid = Number(r.bid);
  const ask = Number(r.ask);
  if (bid > 0 && ask > 0 && ask >= bid) {
    if ((ask - bid) / mid > 0.3) return false;
  }
  return true;
}

function defaultFilters(): PutFilterState {
  return Layout.migrateFilterStateV1toV2(
    {
      quote: "all",
      dte: [],
      earnings: "all",
      maxCollateral: 0,
      ticker: "",
      sectors: [],
      rubricBands: [],
      indexes: Layout.DEFAULT_INDEX_FILTER.slice(),
      caps: [],
      minProfitPct: 0,
    },
    "put",
  ) as PutFilterState;
}

function normalizePutFilters(state: PutFilterState): PutFilterState {
  const minProfitPct =
    typeof state.minProfitPct === "number" && Number.isFinite(state.minProfitPct)
      ? Math.max(0, state.minProfitPct)
      : 0;
  const dteKeys = new Set(PUT_DTE_BUCKETS.map((bucket) => bucket.key));
  let dte: string[];
  if (Array.isArray(state.dte)) {
    dte = state.dte.filter((key) => dteKeys.has(key));
  } else if (typeof (state as { dte?: unknown }).dte === "string") {
    const legacy = (state as { dte: string }).dte;
    dte = legacy && legacy !== "all" && dteKeys.has(legacy) ? [legacy] : [];
  } else {
    dte = [];
  }
  const indexKeys = new Set(Layout.INDEX_FILTER_CODES);
  const indexes = Array.isArray(state.indexes)
    ? state.indexes.filter((key) => indexKeys.has(key))
    : Layout.DEFAULT_INDEX_FILTER.slice();
  return {
    ...state,
    minProfitPct,
    dte,
    indexes,
    ticker: sanitizeTickerFilterValue(state.ticker).toUpperCase(),
  };
}

function loadFilters(): PutFilterState {
  try {
    const raw = localStorage.getItem(PUT_FILTERS_KEY);
    if (!raw) return defaultFilters();
    return normalizePutFilters(
      Layout.migrateFilterStateV1toV2(JSON.parse(raw), "put") as PutFilterState,
    );
  } catch {
    return defaultFilters();
  }
}

function saveFilters(state: PutFilterState): void {
  localStorage.setItem(PUT_FILTERS_KEY, JSON.stringify(state));
}

function loadStarredFilterOn(): boolean {
  try {
    return localStorage.getItem(PUT_STARRED_FILTER_KEY) === "1";
  } catch {
    return false;
  }
}

function saveStarredFilterOn(on: boolean): void {
  localStorage.setItem(PUT_STARRED_FILTER_KEY, on ? "1" : "0");
}

function loadCashLimitFilterOn(): boolean {
  try {
    return localStorage.getItem(PUT_CASH_LIMIT_FILTER_KEY) === "1";
  } catch {
    return false;
  }
}

function saveCashLimitFilterOn(on: boolean): void {
  localStorage.setItem(PUT_CASH_LIMIT_FILTER_KEY, on ? "1" : "0");
}

function loadLocalStarred(): Set<string> {
  try {
    const raw = JSON.parse(localStorage.getItem(PUT_STARRED_KEY) ?? "[]") as unknown;
    if (!Array.isArray(raw)) return new Set();
    return new Set(raw.map((t) => String(t).trim().toUpperCase()).filter(Boolean));
  } catch {
    return new Set();
  }
}

function saveLocalStarred(tickers: Set<string>): void {
  localStorage.setItem(PUT_STARRED_KEY, JSON.stringify([...tickers]));
}

function isStarred(ticker: string): boolean {
  return starredTickers.has(ticker.trim().toUpperCase());
}

function toggleStarred(ticker: string): void {
  const key = ticker.trim().toUpperCase();
  if (!key) return;
  if (starredTickers.has(key)) starredTickers.delete(key);
  else starredTickers.add(key);
  saveLocalStarred(starredTickers);
}

function enrichRow(rec: WheelRecommendation, positions: PositionsSnapshot): WheelRecommendation {
  const ticker = normalizeTicker(rec.underlying_ticker);
  const badges = rec.index_badges?.length ? rec.index_badges : indexBadgesForTicker(ticker);
  let openPuts = rec.open_put_contracts;
  let stockBlocks = rec.stock_blocks ?? rec.covered_call_contracts;
  if (openPuts == null || stockBlocks == null) {
    const puts = positions.options.filter(
      (p) => normalizeTicker(p.ticker) === ticker && /put/i.test(p.side) && !p.expired,
    );
    const stock = positions.stock.find((p) => normalizeTicker(p.ticker) === ticker);
    if (openPuts == null) {
      openPuts = puts.reduce((sum, p) => sum + (Number(p.contracts) || 0), 0);
    }
    if (stockBlocks == null) {
      stockBlocks = stock ? Math.floor((Number(stock.quantity) || 0) / 100) : 0;
    }
  }
  const { held, available } = sharesForTicker(positions, ticker);
  return {
    ...rec,
    index_badges: badges,
    open_put_contracts: openPuts,
    stock_blocks: stockBlocks,
    sfe_shares_held: held,
    sfe_shares_available: available,
  };
}

function putAdviceAttrs(row: WheelRecommendation) {
  return {
    underlying: row.underlying_ticker,
    side: row.side || "put",
    strike: row.strike,
    expiration: row.expiration_date,
    optionSymbol: row.option_symbol,
  };
}

function applyFilters(
  rows: WheelRecommendation[],
  state: PutFilterState,
  availableCash: number | null,
): WheelRecommendation[] {
  let out = Layout.filterRecommendationRows(rows, state, "put", PUT_DTE_BUCKETS, isTradableQuote) as WheelRecommendation[];
  out = out.filter((row) => scopeAllowsRecommendation(row.underlying_ticker, "put"));
  if (starredFilterOn) {
    out = out.filter((r) => isStarred(r.underlying_ticker ?? ""));
  }
  if (cashLimitFilterOn && availableCash != null) {
    if (availableCash <= 0) {
      out = [];
    } else {
      out = out.filter((r) => putCollateral(r) <= availableCash + 1e-6);
    }
  }
  if (state.minProfitPct > 0) {
    out = out.filter((r) => {
      const profit = recommendationProfitPct(r);
      return profit != null && profit >= state.minProfitPct;
    });
  }
  return pinAdvisedScreenerRows(rows, out, "puts", putAdviceAttrs);
}

function mergePutAdviceRows(
  filtered: WheelRecommendation[],
  cache: PutsPanelCache,
): WheelRecommendation[] {
  return appendSyntheticAdviceRows(
    filtered,
    cache.allRows,
    "puts",
    putAdviceAttrs,
    (seed) =>
      enrichAdviceScreenerRow(
        enrichRow(
          {
            ...adviceSeedToScreenerRow(seed),
            side: seed.side || "put",
          },
          cache.positions,
        ),
        cache.allRows,
        cache.positions,
      ),
  );
}

function capsSelectionForUi(caps: string[]): string[] {
  if (!caps.length || caps.length >= Layout.CAP_FILTER_CODES.length) return [];
  return caps;
}

function dteSelectionForUi(dte: string[]): string[] {
  if (!dte.length || dte.length >= PUT_DTE_BUCKETS.length) return [];
  return dte;
}

function indexesSelectionForUi(indexes: string[]): string[] {
  if (!indexes.length || indexes.length >= Layout.INDEX_FILTER_CODES.length) return [];
  return indexes;
}

function renderFilterBar(state: PutFilterState, allRows: WheelRecommendation[]): string {
  return `
    <div class="sfe-puts-filter-rows" data-tour-anchor="wheel-puts-filters">
      <div class="filter-bar recs-filter-bar">
      <div class="filter-field">
        <label for="wheelPutTicker">Ticker<span class="hint" tabindex="0" role="img" data-tip="Prefix match — case-insensitive.">i</span></label>
        <input type="text" id="wheelPutTicker" class="filter-input-compact" placeholder="All"${TICKER_FILTER_INPUT_ATTRS} value="${escAttr(state.ticker)}" />
      </div>
      <div class="filter-field">
        <label>Starred<span class="hint" tabindex="0" role="img" data-tip="Show only tickers you starred in the put table.">i</span></label>
        <div class="year-chips" id="wheelPutStarredFilter">
          <button type="button" class="year-chip${starredFilterOn ? " active" : ""}" data-put-starred-toggle aria-pressed="${starredFilterOn ? "true" : "false"}">Starred</button>
        </div>
      </div>
      <div class="filter-field">
        <label>Earnings<span class="hint" tabindex="0" role="img" data-tip="Hide expirations on or after the next Finviz earnings date.">i</span></label>
        <div class="year-chips" id="wheelPutEarningsFilter">
          <button type="button" class="year-chip${state.earnings === "before" ? " active" : ""}" data-put-earnings-toggle aria-pressed="${state.earnings === "before" ? "true" : "false"}">Before earnings</button>
        </div>
      </div>
      <div class="filter-field">
        <label>Quote<span class="hint" tabindex="0" role="img" data-tip="Only show contracts with a recent two-sided quote (excludes stale / wide-spread).">i</span></label>
        <div class="year-chips" id="wheelPutQuoteFilter">
          <button type="button" class="year-chip${state.quote === "tradable" ? " active" : ""}" data-put-quote-toggle aria-pressed="${state.quote === "tradable" ? "true" : "false"}">Tradable</button>
        </div>
      </div>
      <div class="filter-field">
        <label>Cash limit<span class="hint" tabindex="0" role="img" data-tip="Only show puts whose collateral fits available cash or buying power from the Robinhood Agentic snapshot.">i</span></label>
        <div class="year-chips" id="wheelPutCashLimitFilter">
          <button type="button" class="year-chip${cashLimitFilterOn ? " active" : ""}" data-put-cash-limit-toggle aria-pressed="${cashLimitFilterOn ? "true" : "false"}">Cash limit</button>
        </div>
      </div>
      ${advisoryFilterFieldHtml()}
      <div class="filter-field">
        <label for="wheelPutDteFilter">DTE<span class="hint" tabindex="0" role="img" data-tip="Filter screened contracts by days-to-expiration bucket. Select several ranges to combine.">i</span></label>
        <select multiple id="wheelPutDteFilter" aria-label="Put DTE filter" data-placeholder="All DTE">${multiSelectOptionsWithAll("All DTE", DTE_OPTIONS, dteSelectionForUi(state.dte))}</select>
      </div>
      <div class="filter-field">
        <label for="wheelPutIndexFilter">Index<span class="hint" tabindex="0" role="img" data-tip="Filter by index membership. Select several to combine lists.">i</span></label>
        <select multiple id="wheelPutIndexFilter" aria-label="Put index filter" data-placeholder="All indexes">${multiSelectOptionsWithAll("All indexes", INDEX_OPTIONS, indexesSelectionForUi(state.indexes))}</select>
      </div>
      <div class="filter-field">
        <label for="wheelPutCapFilter">Cap<span class="hint" tabindex="0" role="img" data-tip="Finviz-style market-cap bands.">i</span></label>
        <select multiple id="wheelPutCapFilter" aria-label="Put market cap filter" data-placeholder="All caps">${multiSelectOptionsWithAll("All caps", CAP_OPTIONS, capsSelectionForUi(state.caps))}</select>
      </div>
      </div>
      <div class="filter-bar recs-filter-bar">
      <div class="filter-field">
        <label for="wheelPutSectorFilter">Sector<span class="hint" tabindex="0" role="img" data-tip="Filter screened puts by sector.">i</span></label>
        <select multiple id="wheelPutSectorFilter" aria-label="Put sector filter" data-placeholder="All sectors">${sectorOptions(allRows, state.sectors)}</select>
      </div>
      <div class="filter-field">
        <label for="wheelPutRubricFilter">Rubric band<span class="hint" tabindex="0" role="img" data-tip="Latest stored fundamental report rubric band per ticker.">i</span></label>
        <select multiple id="wheelPutRubricFilter" aria-label="Put rubric band filter" data-placeholder="All bands">${multiSelectOptionsWithAll("All bands", RUBRIC_OPTIONS, state.rubricBands)}</select>
      </div>
      <div class="filter-field">
        <label for="wheelPutMinProfit">Min profit.<span class="hint" tabindex="0" role="img" data-tip="Minimum premium yield on cash collateral: (last option price × 100) ÷ collateral.">i</span></label>
        <span class="filter-affix">
          <input type="number" id="wheelPutMinProfit" class="filter-input-compact" step="0.1" min="0" inputmode="decimal" aria-label="Minimum profitability percent" value="${state.minProfitPct > 0 ? state.minProfitPct : ""}" />
          <span class="filter-affix__suffix">%</span>
        </span>
      </div>
      <div class="filter-field">
        <div class="sfe-filter-reset-actions sfe-web-puts-columns-host">
          <button type="button" class="small sfe-reset-filters" id="wheelPutResetFilters" aria-label="Reset filters" title="Reset filters"><svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 5h11l-4.2 5.2V16l-2.6 1.4V10.2L3 5z"/><path d="m15 15 6 6"/><path d="m21 15-6 6"/></svg></button>
          ${screenerAdviceButtonHtml()}
        </div>
      </div>
      </div>
    </div>`;
}

function sectorOptions(rows: WheelRecommendation[], selected: string[]): string {
  const counts = new Map<string, number>();
  for (const r of rows) {
    const s = r.sector?.trim() || "Unknown";
    counts.set(s, (counts.get(s) ?? 0) + 1);
  }
  const opts = [...counts.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([sector, count]) => {
      const sel = selected.includes(sector) ? " selected" : "";
      return `<option value="${escAttr(sector)}"${sel}>${esc(sector)} (${count})</option>`;
    });
  const allSelected = !selected.length ? " selected" : "";
  return `<option value="" data-all="1"${allSelected}>All sectors</option>${opts.join("")}`;
}

function multiSelectOptionsWithAll(
  allLabel: string,
  options: { value: string; label: string }[],
  selected: string[],
): string {
  const allSelected = !selected.length ? " selected" : "";
  const allOpt = `<option value="" data-all="1"${allSelected}>${esc(allLabel)}</option>`;
  const opts = options
    .map((o) => {
      const sel = selected.includes(o.value) ? " selected" : "";
      return `<option value="${escAttr(o.value)}"${sel}>${esc(o.label)}</option>`;
    })
    .join("");
  return allOpt + opts;
}

function visibleColumnIds(columns: ColumnState[]): string[] {
  const lockedLast = "_actions";
  const ids = displayTableColumns(columns, isWebCompactMode()).map((column) => column.key);
  if (!ids.includes(lockedLast)) ids.push(lockedLast);
  else {
    const i = ids.indexOf(lockedLast);
    if (i >= 0 && i !== ids.length - 1) {
      ids.splice(i, 1);
      ids.push(lockedLast);
    }
  }
  if (!ids.includes("underlying_ticker")) ids.unshift("underlying_ticker");
  return ids.filter((id) => id !== "dte");
}

function renderHeader(columns: ColumnState[]): string {
  const meta = new Map(Layout.PUT_COLUMN_META.map((m) => [m.id, m]));
  const compact = isWebCompactMode();
  const ids = visibleColumnIds(columns);
  return ids
    .map((colId) => {
      const m = meta.get(colId) ?? EXTRA_COLUMN_META[colId];
      if (colId === "_actions") return '<th data-col="_actions"></th>';
      if (!m) return "";
      const clsParts: string[] = [];
      if (m.num) clsParts.push("num");
      if (m.heldTh) clsParts.push("held-th");
      if (colId === "underlying_ticker") {
        clsParts.push("recs-sticky-col", "sfe-ticker-col-header");
      }
      if (colId === "next_earnings_date") clsParts.push("sfe-earnings-header");
      if (colId === "broker_mark" || colId === "account_short") clsParts.push("sfe-mark-col");
      if (colId === "account_short") clsParts.push("sfe-acct-col");
      const cls = clsParts.length ? ` class="${clsParts.join(" ")}"` : "";
      const sortKey = "sortKey" in m ? m.sortKey : undefined;
      const sortType = "sortType" in m ? m.sortType : undefined;
      const sortAttr = sortKey ? ` data-sort="${sortKey}" data-type="${sortType ?? "str"}"` : "";
      const hint = Layout.columnHeaderHint(m, compact);
      const tipAttr = hint ? ` data-tip="${escAttr(hint)}"` : "";
      return `<th data-col="${colId}"${sortAttr}${tipAttr}${cls}><span class="th-inner"><span class="th-label">${esc(m.label)}</span></span></th>`;
    })
    .join("");
}


function sharesCell(rec: WheelRecommendation, colId: "held" | "open"): string {
  return renderScreenerSharesCellHtml(rec.sfe_shares_held, rec.sfe_shares_available, colId, esc);
}

function rubricCell(rec: WheelRecommendation): string {
  const tk = esc(rec.underlying_ticker ?? "");
  const st = rec.report_state;
  if (st === "missing") {
    return `<td class="rubric-cell" data-col="decision"><button type="button" class="muted wheel-rubric-missing" data-ticker="${tk}">No report</button></td>`;
  }
  if (st && st !== "available") {
    return `<td class="rubric-cell" data-col="decision"><span class="muted wheel-rubric-unknown">Band unavailable</span></td>`;
  }
  const decision = rec.decision?.trim() ?? "";
  if (!decision || !Layout.KNOWN_DECISIONS.has(decision)) {
    const label = decision ? "Unmapped band" : "Band unavailable";
    const cls = decision ? "wheel-rubric-unmapped" : "wheel-rubric-unavailable";
    return `<td class="rubric-cell" data-col="decision"><button type="button" class="wheel-rubric-link muted ${cls}" data-ticker="${tk}">${esc(label)}</button></td>`;
  }
  const disp = Layout.displayDecision(decision);
  const tier = Layout.verdictTier(decision);
  return `<td class="rubric-cell" data-col="decision"><button type="button" class="wheel-rubric-link wheel-rubric-${tier}" data-ticker="${tk}">${esc(disp)}</button></td>`;
}

function expiryCell(rec: WheelRecommendation, compact: boolean): string {
  const inner = renderScreenerExpiryInnerHtml(rec, esc);
  const cls = compact ? "sfe-web-compact-cell" : "sfe-screener-expiry-cell";
  const tip = screenerExpiryEarningsTip(rec);
  const tipAttr = tip ? ` data-tip="${escAttr(tip)}"` : "";
  const expDate = screenerExpiryDateLabel(rec.expiration_date);
  const dateAttr = expDate !== "—" ? ` data-expiration-date="${escAttr(expDate)}"` : "";
  return `<td class="${cls}" data-col="expiration_date"${dateAttr}${tipAttr}>${inner}</td>`;
}

function targetCell(rec: WheelRecommendation): string {
  return `<td class="num" data-col="target_price">${fmtUsd(rec.target_price)}</td>`;
}

function roiCell(roi: number | undefined): string {
  const v = Number(roi);
  const cls = !Number.isNaN(v) && v >= 0.25 ? "roi-strong" : v >= 0.12 ? "roi-mid" : "";
  return `<td class="num ${cls}" data-col="roi">${fmtWheelRatio(roi)}</td>`;
}

function earningsCell(rec: WheelRecommendation): string {
  const date = fmtEarningsDate(rec.next_earnings_date);
  const tip = earningsDateTip(rec);
  const within = earningsWithinExpiry(rec);
  const withinAttr = within ? ' data-earnings-within-expiry="true"' : "";
  if (date === "—") {
    return `<td class="sfe-earnings-cell sfe-web-compact-cell" data-col="next_earnings_date" data-tip="${escAttr(tip)}"${withinAttr}>—</td>`;
  }
  const inDays = fmtEarningsInDays(rec.next_earnings_date);
  const subCls = within ? "sfe-earnings-within-expiry" : "";
  const inner = `${esc(date)}${compactSubline(inDays, subCls)}`;
  return `<td class="sfe-earnings-cell sfe-web-compact-cell" data-col="next_earnings_date" data-tip="${escAttr(tip)}"${withinAttr}>${inner}</td>`;
}

function underlyingCell(rec: WheelRecommendation, idx: number): string {
  const ticker = rec.underlying_ticker ?? "—";
  const on = isStarred(ticker);
  const starHtml =
    ticker !== "—"
      ? `<button type="button" class="wheel-star-btn${on ? " is-starred" : ""}" data-star-ticker="${escAttr(ticker.toUpperCase())}" aria-pressed="${on ? "true" : "false"}" title="Star for your shortlist filter">${on ? "★" : "☆"}</button>`
      : "";
  const badges = renderIndexBadgesHtml(rec.index_badges);
  const advised =
    rec.sfe_advice ||
    screenerRowIsAdvised(
      {
        underlying: rec.underlying_ticker,
        side: rec.side || "put",
        strike: rec.strike,
        expiration: rec.expiration_date,
        optionSymbol: rec.option_symbol,
      },
      "puts",
    );
  const adviceBadge = advised ? adviceBadgeHtml() : "";
  const tickerHtml = `<span class="recs-ticker${rec.is_golden ? " is-golden" : ""}" data-ticker="${escAttr(ticker)}" data-side="put" data-row-idx="${idx}" title="Open ${escAttr(ticker)} on Stock Forge">${esc(ticker)}<span class="tk-ic">↗</span></span>`;
  const compactCls = isWebCompactMode() ? " sfe-web-compact-cell" : "";
  return `<td class="tk-cell${compactCls}" data-col="underlying_ticker"><div class="tk-row">${starHtml}${tickerHtml}${badges}${adviceBadge}</div></td>`;
}

function underlyingPriceCell(rec: WheelRecommendation, compact: boolean): string {
  if (compact) {
    return `<td class="num sfe-web-compact-cell" data-col="underlying_price">${fmtUsd(rec.underlying_price)}${compactSubline(`tgt ${fmtUsd(rec.target_price)}`, "sfe-web-compact-sub")}</td>`;
  }
  return `<td class="num" data-col="underlying_price">${fmtUsd(rec.underlying_price)}</td>`;
}

function renderCell(colId: string, rec: WheelRecommendation, idx: number): string {
  if (colId === "broker_mark") return brokerMarkCellHtml(recommendationLedgerMark(rec.underlying_ticker));
  if (colId === "account_short") return accountShortCellHtml(recommendationLedgerMark(rec.underlying_ticker));
  const compact = isWebCompactMode();
  switch (colId) {
    case "underlying_ticker":
      return underlyingCell(rec, idx);
    case "market_cap":
      return `<td class="num" data-col="market_cap">${Layout.fmtMktCapBillions(rec.market_cap)}</td>`;
    case "sector": {
      const s = rec.sector?.trim();
      return s
        ? `<td class="sector-cell" data-col="sector" title="${escAttr(s)}">${esc(s)}</td>`
        : `<td class="sector-cell muted" data-col="sector">—</td>`;
    }
    case "strike": {
      if (compact) {
        return `<td class="num sfe-web-compact-cell" data-col="strike">${fmtNum(rec.strike, 2)}${compactSubline(`POP: ${fmtWheelRatio(rec.pop)}`, "sfe-web-compact-sub")}</td>`;
      }
      return `<td class="num" data-col="strike">${fmtNum(rec.strike, 2)}</td>`;
    }
    case "underlying_price":
      return underlyingPriceCell(rec, compact);
    case "target_price":
      return targetCell(rec);
    case "decision":
      return rubricCell(rec);
    case "expiration_date":
      return expiryCell(rec, compact);
    case "next_earnings_date":
      return earningsCell(rec);
    case "dte":
      return `<td class="num" data-col="dte">${rec.dte != null ? rec.dte : "—"}</td>`;
    case "mid": {
      if (compact) {
        return `<td class="num sfe-web-compact-cell" data-col="mid">${fmtUsd(rec.mid)}${compactSubline(`last ${fmtUsd(rec.last_price)}`, "sfe-web-compact-sub")}</td>`;
      }
      return `<td class="num" data-col="mid">${fmtUsd(rec.mid)}</td>`;
    }
    case "last_price":
      return `<td class="num" data-col="last_price">${fmtUsd(rec.last_price)}</td>`;
    case "rec_quantity":
      return `<td class="num" data-col="rec_quantity">${rec.rec_quantity != null ? rec.rec_quantity : "—"}</td>`;
    case "collateral":
      return `<td class="num" data-col="collateral">${fmtUsd(rec.collateral)}</td>`;
    case "roi": {
      if (compact) {
        const v = Number(rec.roi);
        const cls = !Number.isNaN(v) && v >= 0.25 ? "roi-strong" : v >= 0.12 ? "roi-mid" : "sfe-web-compact-sub";
        return `<td class="num sfe-web-compact-cell" data-col="roi">${fmtWheelRatio(rec.roi)}${compactSubline(fmtPremiumPerDay(rec), cls)}</td>`;
      }
      return roiCell(rec.roi);
    }
    case "pop":
      return `<td class="num" data-col="pop">${fmtWheelRatio(rec.pop)}</td>`;
    case "held":
      return sharesCell(rec, "held");
    case "_actions":
      return `<td class="pos-actions" data-col="_actions">${renderFocusRecRowMenu({ ...rec, side: rec.side || "put" })}</td>`;
    default:
      return `<td data-col="${colId}">—</td>`;
  }
}

function putContractKey(rec: WheelRecommendation): string {
  return screenerContractKey({
    underlying: rec.underlying_ticker,
    side: rec.side || "put",
    strike: rec.strike,
    expiration: rec.expiration_date,
    optionSymbol: rec.option_symbol,
  });
}

function renderPutRow(rec: WheelRecommendation, index: number, columns: ColumnState[]): string {
  const ids = visibleColumnIds(columns);
  const cells = ids.map((id) => renderCell(id, rec, index)).join("");
  const advised =
    rec.sfe_advice ||
    screenerRowIsAdvised(
      {
        underlying: rec.underlying_ticker,
        side: rec.side || "put",
        strike: rec.strike,
        expiration: rec.expiration_date,
        optionSymbol: rec.option_symbol,
      },
      "puts",
    );
  const rowClass = rec.sfe_advice ? "sfe-row-ai-advice" : advised ? "sfe-row-advised-line" : "";
  const key = putContractKey(rec);
  return `<tr class="${rowClass}" ${adviceRowAttrs({
    underlying: rec.underlying_ticker,
    side: rec.side || "put",
    strike: rec.strike,
    expiration: rec.expiration_date,
    optionSymbol: rec.option_symbol,
  })} data-row-key="${escAttr(key)}" data-row-idx="${index}"${rec.sfe_advice ? ' data-sfe-advice-synthetic="1"' : ""}>${cells}</tr>`;
}

function renderRows(rows: WheelRecommendation[], columns: ColumnState[]): string {
  const ids = visibleColumnIds(columns);
  if (!rows.length) {
    return `<tr><td colspan="${Math.max(ids.length, 1)}" class="muted">No puts match your filters.</td></tr>`;
  }
  return rows.map((rec, i) => renderPutRow(rec, i, columns)).join("");
}

function putsFilterNoteText(
  count: number,
  opts: { stale?: boolean; availableCash?: number | null; cashLoading?: boolean } = {},
): string {
  const staleNote = opts.stale ? " · showing cached data, refreshing…" : "";
  let cashNote = "";
  if (cashLimitFilterOn) {
    if (opts.cashLoading) {
      cashNote = " · loading Robinhood cash…";
    } else if (opts.availableCash != null && opts.availableCash > 0) {
      cashNote = ` · cash limit ${fmtUsd(opts.availableCash)}`;
    } else if (opts.availableCash === 0) {
      cashNote = " · no available cash in Agentic snapshot";
    }
  }
  return `${count} contract${count === 1 ? "" : "s"}${cashNote}${staleNote}`;
}

function existingRowKey(tr: HTMLTableRowElement): string {
  const stored = (tr.getAttribute("data-row-key") || "").trim();
  if (stored) return stored;
  return screenerContractKey({
    underlying: tr.getAttribute("data-underlying"),
    side: tr.getAttribute("data-side") || "put",
    strike: Number(tr.getAttribute("data-strike")),
    expiration: tr.getAttribute("data-exp"),
    optionSymbol: tr.getAttribute("data-option-symbol"),
  });
}

function panelHasDisplayedContent(panel: HTMLElement): boolean {
  return panel.querySelector(".sfe-web-native-puts") != null;
}

function patchPutRow(tr: HTMLTableRowElement, rec: WheelRecommendation, index: number): void {
  const columns = resolveWebTableColumns(TABLE_ID);
  const key = putContractKey(rec);
  if (key) tr.setAttribute("data-row-key", key);
  for (const colId of visibleColumnIds(columns)) {
    if (colId === "_actions" || colId === "underlying_ticker") continue;
    const td = tr.querySelector<HTMLTableCellElement>(`td[data-col="${colId}"]`);
    if (!td) continue;
    morphTableCell(td, renderCell(colId, rec, index));
  }
  const advised =
    rec.sfe_advice ||
    screenerRowIsAdvised(
      {
        underlying: rec.underlying_ticker,
        side: rec.side || "put",
        strike: rec.strike,
        expiration: rec.expiration_date,
        optionSymbol: rec.option_symbol,
      },
      "puts",
    );
  tr.classList.toggle("sfe-row-ai-advice", Boolean(rec.sfe_advice));
  tr.classList.toggle("sfe-row-advised-line", Boolean(advised && !rec.sfe_advice));
  tr.setAttribute("data-row-idx", String(index));
}

function patchPutsTableBody(
  tbody: HTMLElement,
  rows: WheelRecommendation[],
  columns: ColumnState[],
): void {
  reconcileTableBodyByKey(tbody, rows, {
    rowSelector: "tr[data-option-symbol], tr[data-underlying], tr[data-row-key]",
    rowKey: putContractKey,
    existingKey: existingRowKey,
    emptyHtml: `<tr><td colspan="${Math.max(visibleColumnIds(columns).length, 1)}" class="muted">No puts match your filters.</td></tr>`,
    createRow: (rec, index) => rowFromHtml(renderPutRow(rec, index, columns)),
    patchRow: patchPutRow,
  });
}

function currentPutsRows(
  cache: PutsPanelCache,
  state: PutFilterState,
): WheelRecommendation[] {
  const availableCash = cashLimitFilterOn ? cachedAvailableCash : null;
  return mergePutAdviceRows(applyFilters(cache.allRows, state, availableCash), cache);
}

function softUpdatePutsPanel(
  panel: HTMLElement,
  state: PutFilterState,
  cache: PutsPanelCache,
): void {
  putsPanelCache = cache;
  const columns = resolveWebTableColumns(TABLE_ID);
  const filtered = currentPutsRows(cache, state);
  const tbody = panel.querySelector<HTMLElement>("#wheelPutsBody");
  if (tbody) patchPutsTableBody(tbody, filtered, columns);
  const note = panel.querySelector<HTMLElement>("#wheelPutFilterNote");
  if (note) {
    note.textContent = putsFilterNoteText(filtered.length, {
      availableCash: cashLimitFilterOn ? cachedAvailableCash : null,
    });
  }
  refreshPutsTabCashCard(panel);
  applyAdviceMarks(panel, "puts");
}

function renderPanelShell(
  state: PutFilterState,
  allRows: WheelRecommendation[],
  filtered: WheelRecommendation[],
  columns: ColumnState[],
  opts: { stale?: boolean; availableCash?: number | null; cashLoading?: boolean } = {},
): string {
  const head = renderHeader(columns);
  const body = renderRows(filtered, columns);
  return `
    <div data-wheel-view="puts" class="panel sfe-web-native-puts" style="margin-top:12px;">
      <div class="sfe-puts-screener-head">
        <h2 data-tour-anchor="wheel-puts-heading">Put contract screener<span class="hint" tabindex="0" role="img" data-tip="Cash-secured put contracts from your watchlist that pass your screen settings. For independent review only — not a recommendation.">i</span></h2>
        ${putsTabCashCardHtml()}
      </div>
      ${renderFilterBar(state, allRows)}
      <p class="muted small recs-filter-note" id="wheelPutFilterNote">${putsFilterNoteText(filtered.length, opts)}</p>
      <div class="recs-wrap" data-tour-anchor="wheel-puts-table">
        <div class="recs-hscroll" id="wheelPutsHScroll">
          <table class="recs recs-compact recs-sticky-col wheel-screener-puts" id="wheelPutsTable"${renderTablePinnedAttr(columns)}>
            <thead><tr id="wheelPutsHeadRow">${head}</tr></thead>
            <tbody id="wheelPutsBody">${body}</tbody>
          </table>
        </div>
      </div>
    </div>`;
}

function readMultiSelect(el: HTMLSelectElement | null): string[] {
  if (!el) return [];
  return [...el.selectedOptions].map((o) => o.value).filter(Boolean);
}

function enhancePutFilterSelects(root: HTMLElement): void {
  const api = (window as Window & { UnifiedMultiSelect?: { enhanceAll: (scope?: ParentNode) => void } })
    .UnifiedMultiSelect;
  api?.enhanceAll(root);
}

/** Remove body-portaled dropdown menus before puts panel re-render. */
export function teardownPutsMultiSelectPortals(root?: ParentNode | null): void {
  teardownMultiSelectPortals(root);
}

function bindFilters(
  root: HTMLElement,
  state: PutFilterState,
  rerender: () => void,
  onCashLimitChanged: () => void,
): void {
  root.querySelector("[data-put-quote-toggle]")?.addEventListener("click", () => {
    state.quote = state.quote === "tradable" ? "all" : "tradable";
    saveFilters(state);
    rerender();
  });

  root.querySelector("[data-put-earnings-toggle]")?.addEventListener("click", () => {
    state.earnings = state.earnings === "before" ? "all" : "before";
    saveFilters(state);
    rerender();
  });

  const dte = root.querySelector<HTMLSelectElement>("#wheelPutDteFilter");
  dte?.addEventListener("change", () => {
    state.dte = readMultiSelect(dte);
    saveFilters(state);
    rerender();
  });

  const ticker = root.querySelector<HTMLInputElement>("#wheelPutTicker");
  ticker?.addEventListener("input", () => {
    state.ticker = sanitizeTickerFilterValue(ticker.value).toUpperCase();
    saveFilters(state);
    rerender();
  });

  guardTickerFilterInputs(root);

  const index = root.querySelector<HTMLSelectElement>("#wheelPutIndexFilter");
  index?.addEventListener("change", () => {
    state.indexes = readMultiSelect(index);
    saveFilters(state);
    rerender();
  });

  root.querySelector("[data-put-starred-toggle]")?.addEventListener("click", () => {
    starredFilterOn = !starredFilterOn;
    saveStarredFilterOn(starredFilterOn);
    rerender();
  });

  root.querySelector("[data-put-cash-limit-toggle]")?.addEventListener("click", () => {
    cashLimitFilterOn = !cashLimitFilterOn;
    saveCashLimitFilterOn(cashLimitFilterOn);
    onCashLimitChanged();
  });

  const minProfit = root.querySelector<HTMLInputElement>("#wheelPutMinProfit");
  minProfit?.addEventListener("input", () => {
    const parsed = Number(minProfit.value);
    state.minProfitPct =
      minProfit.value.trim() !== "" && Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
    saveFilters(state);
    rerender();
  });

  const cap = root.querySelector<HTMLSelectElement>("#wheelPutCapFilter");
  cap?.addEventListener("change", () => {
    state.caps = readMultiSelect(cap);
    saveFilters(state);
    rerender();
  });

  const sector = root.querySelector<HTMLSelectElement>("#wheelPutSectorFilter");
  sector?.addEventListener("change", () => {
    state.sectors = readMultiSelect(sector);
    saveFilters(state);
    rerender();
  });

  const rubric = root.querySelector<HTMLSelectElement>("#wheelPutRubricFilter");
  rubric?.addEventListener("change", () => {
    state.rubricBands = readMultiSelect(rubric);
    saveFilters(state);
    rerender();
  });

  root.querySelector("#wheelPutResetFilters")?.addEventListener("click", () => {
    Object.assign(state, defaultFilters());
    starredFilterOn = false;
    cashLimitFilterOn = false;
    saveStarredFilterOn(false);
    saveCashLimitFilterOn(false);
    saveFilters(state);
    onCashLimitChanged();
  });

  root.querySelector("#wheelPutsBody")?.addEventListener("click", (event) => {
    const star = (event.target as HTMLElement).closest<HTMLButtonElement>(".wheel-star-btn");
    if (star?.dataset.starTicker) {
      toggleStarred(star.dataset.starTicker);
      rerender();
    }
  });
}

function buildPutsPanelCacheFromPeek(): PutsPanelCache | null {
  const recSnap = peekRecommendationsSnapshot();
  const positions = peekPositionsSnapshot();
  if (!recSnap || !positions) return null;
  return {
    allRows: recSnap.puts.map((r) => enrichRow(r, positions)),
    positions,
    fetchedAt: Math.max(recSnap.fetchedAt, positions.fetchedAt),
  };
}

function mountPutsPanel(
  panel: HTMLElement,
  state: PutFilterState,
  cache: PutsPanelCache,
  opts: { stale?: boolean } = {},
): () => void {
  putsPanelCache = cache;
  let showStaleBanner = opts.stale ?? false;
  let availableCash: number | null = cashLimitFilterOn ? cachedAvailableCash : null;
  let cashLoading = cashLimitFilterOn && availableCash == null;

  const rerender = (): void => {
    const live = putsPanelCache ?? cache;
    teardownPutsMultiSelectPortals(panel);
    const columns = resolveWebTableColumns(TABLE_ID);
    const filtered = mergePutAdviceRows(applyFilters(live.allRows, state, availableCash), live);
    panel.innerHTML = renderPanelShell(state, live.allRows, filtered, columns, {
      stale: showStaleBanner,
      availableCash,
      cashLoading,
    });
    showStaleBanner = false;
    bindFilters(panel, state, rerender, refreshCashIfNeeded);
    bindScreenerAdvice(panel, "puts");
    enhancePutFilterSelects(panel);
    const resetBtn = panel.querySelector<HTMLElement>("#wheelPutResetFilters");
    if (resetBtn) {
      ensureColumnPickerBesideReset(resetBtn, {
        tableId: TABLE_ID,
        title: "Configure Puts columns",
        defaults: getTableColumnDefaults(TABLE_ID),
        onApply: () => rerender(),
      });
    }
    refreshPutsTabCashCard(panel);
    void hydrateAdviceScreenerRows(filtered).then((changed) => {
      if (changed && putsMountedPanel === panel && putsMountedPanel.getAttribute("data-wheel-view") === "puts") {
        rerender();
      }
    });
  };

  const refreshCashIfNeeded = (): void => {
    if (!cashLimitFilterOn) {
      availableCash = null;
      cashLoading = false;
      rerender();
      return;
    }
    if (cachedAvailableCash != null && Date.now() - cachedAvailableCashAt < PUTS_CASH_CACHE_MS) {
      availableCash = cachedAvailableCash;
      cashLoading = false;
      rerender();
      return;
    }
    cashLoading = true;
    rerender();
    void loadAvailableCash().then((cash) => {
      availableCash = cash;
      cashLoading = false;
      rerender();
    });
  };

  putsRerender = rerender;
  rerender();
  refreshCashIfNeeded();
  return rerender;
}

async function loadPutsPanelCache(force: boolean): Promise<PutsPanelCache> {
  if (
    !force &&
    putsPanelCache &&
    Date.now() - putsPanelCache.fetchedAt < PUTS_CACHE_MS
  ) {
    return putsPanelCache;
  }

  if (putsPanelInflight && !force) {
    return putsPanelInflight;
  }

  const load = async (): Promise<PutsPanelCache> => {
    try {
      const [recSnap, positions] = await Promise.all([
        loadRecommendationsSnapshot(force),
        loadPositionsSnapshot(force),
        ensureLedgerMarkIndex(force).catch(() => null),
      ]);
      const cache: PutsPanelCache = {
        allRows: recSnap.puts.map((r) => enrichRow(r, positions)),
        positions,
        fetchedAt: Date.now(),
      };
      putsPanelCache = cache;
      return cache;
    } catch (err) {
      if (putsPanelCache) return putsPanelCache;
      throw err;
    } finally {
      putsPanelInflight = null;
    }
  };

  putsPanelInflight = load();
  return putsPanelInflight;
}

export async function renderNativePutsPanel(
  panel: HTMLElement,
  opts: { force?: boolean } = {},
): Promise<void> {
  const force = opts.force ?? false;
  const state = loadFilters();
  syncStarredFromStorage();
  refreshStarredTickersInBackground(() => {
    if (putsMountedPanel === panel && putsPanelCache) {
      if (panelHasDisplayedContent(panel)) softUpdatePutsPanel(panel, state, putsPanelCache);
      else mountPutsPanel(panel, state, putsPanelCache);
    }
  });

  putsMountedPanel = panel;

  if (!putsPanelCache) {
    const peek = buildPutsPanelCacheFromPeek();
    if (peek) putsPanelCache = peek;
  }

  const hasCached = Boolean(putsPanelCache);
  const displayed = panelHasDisplayedContent(panel);
  if (!hasCached && !displayed) {
    panel.innerHTML = `<div class="sfe-web-panel-loading">Loading put recommendations…</div>`;
  }

  try {
    if (hasCached && !force) {
      if (!displayed) mountPutsPanel(panel, state, putsPanelCache!);
      if (Date.now() - putsPanelCache!.fetchedAt >= PUTS_CACHE_MS) {
        void loadPutsPanelCache(false).then((cache) => {
          if (putsMountedPanel !== panel) return;
          if (panelHasDisplayedContent(panel)) softUpdatePutsPanel(panel, state, cache);
          else mountPutsPanel(panel, state, cache);
        });
      }
      return;
    }

    if (hasCached && displayed) {
      try {
        const cache = await loadPutsPanelCache(force);
        if (putsMountedPanel !== panel) return;
        softUpdatePutsPanel(panel, state, cache);
        return;
      } catch {
        return;
      }
    }

    if (hasCached) {
      mountPutsPanel(panel, state, putsPanelCache!, { stale: true });
    }

    const cache = await loadPutsPanelCache(force);
    if (putsMountedPanel !== panel) return;
    mountPutsPanel(panel, state, cache);
  } catch (err) {
    if (putsMountedPanel !== panel) return;
    paintPanelLoadError(panel, err, {
      title: "Put contract screener",
      view: "puts",
      fallback: "Could not load put recommendations.",
    });
  }
}

export async function refreshNativePutsPanelInBackground(panel: HTMLElement): Promise<void> {
  putsMountedPanel = panel;
  invalidateWheelSnapshots();
  const state = loadFilters();
  syncStarredFromStorage();
  if (putsPanelCache && panelHasDisplayedContent(panel)) {
    try {
      const cache = await loadPutsPanelCache(true);
      if (putsMountedPanel === panel) softUpdatePutsPanel(panel, state, cache);
    } catch {
      /* keep showing current rows */
    }
    return;
  }
  await renderNativePutsPanel(panel, { force: true });
}

export function invalidatePutsPanelCache(): void {
  putsPanelCache = null;
  putsPanelInflight = null;
  cachedAvailableCash = null;
  cachedAvailableCashAt = 0;
  availableCashInflight = null;
  invalidatePutsTabCashCardCache();
  invalidateWheelSnapshots();
}
